<template>
  <div class="modal modal-payment-settings" :class="{ 'show': showModal }">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Edit Commission Setting</h5>
          <button type="button" class="close" @click="showModal = false">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <form @submit.prevent="updateSetting">
          <div class="modal-body">
            <!-- Commission Rules -->
            <h5>Commission Rules</h5>
            <div class="form-row" v-for="(rule, index) in commissionSetting.rules" :key="'rule_' + index">
              <div class="form-group col-md-4">
                <input type="number" step="0.01" class="form-control" v-model="rule.min_payment" placeholder="Min Payment">
              </div>
              <div class="form-group col-md-4">
                <input v-model="rule.max_payment" type="number" step="0.01" class="form-control" placeholder="Max Students" @change="adjustMaxValue(rule, 'min_payment', 'max_payment')">
              </div>
              <div class="form-group col-md-2">
                <input type="number" step="0.01" class="form-control" v-model="rule.commission" placeholder="Commission">
              </div>
              <div class="form-group col-md-2 d-flex">
                <button v-if="index !== 0" type="button" class="btn btn-danger btn-delete" @click="removeItem('rules', index)">
                  <i class="fa fa-trash"></i>
                </button>
                <button v-if="index === commissionSetting.rules.length - 1" type="button" class="btn btn-primary btn-add" @click="addItem('rules')">
                  <i class="fa fa-plus"></i>
                </button>
              </div>
            </div>

            <!-- Enhancers -->
            <h5>Enhancers</h5>
            <div class="form-row" v-for="(enhancer, index) in commissionSetting.enhancers" :key="'enhancer_' + index">
              <div class="form-group col-md-6">
                <v-select v-model="enhancer.type" :options="filteredEnhancerOptions(index)" placeholder="Select Enhancer Type" :clearable="false" />
              </div>
              <div class="form-group col-md-4">
                <input type="number" step="0.01" class="form-control" v-model="enhancer.percentage" placeholder="Percentage">
              </div>
              <div class="form-group col-md-2 d-flex">
                <button v-if="index !== 0" type="button" class="btn btn-danger btn-delete" @click="removeItem('enhancers', index)">
                  <i class="fa fa-trash"></i>
                </button>
                <button v-if="index === commissionSetting.enhancers.length - 1 && commissionSetting.enhancers.length < enhancerOptions.length" type="button" class="btn btn-primary btn-add" @click="addItem('enhancers')">
                  <i class="fa fa-plus"></i>
                </button>
              </div>
            </div>

            <!-- Level Enhancers -->
            <div :class="{ 'd-none': isLevelEnhancersHidden }">
              <h5>Level Enhancers</h5>
              <div class="form-row" v-for="(enhancer, index) in commissionSetting.level_enhancers" :key="enhancer.name + index" ref="levelEnhancersDisplay">
                <div class="form-group col-md-6">
                  <v-select v-model="enhancer.option" :options="filteredEnhancerLevelOptions(index)" item-text="label" item-value="value" placeholder="Select Enhancer Level" :clearable="false" />
                </div>
                <div class="form-group col-md-4">
                  <input type="number" step="0.01" class="form-control" v-model="enhancer.percentage" placeholder="Percentage">
                </div>
                <div class="form-group col-md-2 d-flex">
                  <button v-if="index !== 0" type="button" class="btn btn-danger btn-delete" @click="removeItem('level_enhancers', index)">
                    <i class="fa fa-trash"></i>
                  </button>
                  <button v-if="index === commissionSetting.level_enhancers.length - 1 && commissionSetting.level_enhancers.length < enhancerLevelOptions.length" type="button" class="btn btn-primary btn-add" @click="addItem('level_enhancers')">
                    <i class="fa fa-plus"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Quantity Enhancers -->
            <h5>Quantity Enhancers</h5>
            <div class="form-row" v-for="(qtyEnhancer, index) in commissionSetting.qty_enhancers" :key="'qtyEnhancer_' + index">
              <div class="form-group col-md-3">
                <input type="number" class="form-control" v-model="qtyEnhancer.min_students" placeholder="Min Students">
              </div>
              <div class="form-group col-md-3">
                <input v-model="qtyEnhancer.max_students" type="number" class="form-control" placeholder="Max Students" @change="adjustMaxValue(qtyEnhancer, 'min_students', 'max_students')">
              </div>
              <div class="form-group col-md-4">
                <input type="number" step="0.01" class="form-control" v-model="qtyEnhancer.percentage" placeholder="Percentage">
              </div>
              <div class="form-group col-md-2 d-flex">
                <button v-if="index !== 0" type="button" class="btn btn-danger btn-delete" @click="removeItem('qty_enhancers', index)">
                  <i class="fa fa-trash"></i>
                </button>
                <button v-if="index === commissionSetting.qty_enhancers.length - 1" type="button" class="btn btn-primary btn-add" @click="addItem('qty_enhancers')">
                  <i class="fa fa-plus"></i>
                </button>
              </div>
            </div>

            <!-- School Types -->
            <h5>School Types</h5>
            <div class="form-row" v-for="(type, index) in commissionSetting.school_types" :key="'school_type_' + index">
              <div class="form-group col-md-10">
                <v-select v-model="type.school_type" :options="filteredSchoolTypeOptions(index)" placeholder="Select School Type" @change="handleSchoolTypeChange(index)" />
              </div>
              <div v-if="type.paymentRules.length > 0" class="form-group col-md-2 d-flex">
                <button v-if="index !== 0" type="button" class="btn btn-danger btn-delete" @click="removeItem('school_types', index)">
                  <i class="fa fa-trash"></i>
                </button>
                <button v-if="index === commissionSetting.school_types.length - 1 && commissionSetting.school_types.length < schoolTypeOptions.length" type="button" class="btn btn-primary btn-add" @click="addItem('school_types')">
                  <i class="fa fa-plus"></i>
                </button>
              </div>
              <div v-if="type.school_type" class="form-group col-12">
                <div class="form-row" v-for="(rule, idx) in type.paymentRules" :key="'payment_rule_' + idx">
                  <div class="form-group col-md-4">
                    <input type="number" step="0.01" class="form-control" v-model="rule.min_payment" placeholder="Min Payment">
                  </div>
                  <div class="form-group col-md-4">
                    <input v-model="rule.max_payment" type="number" step="0.01" class="form-control" placeholder="Max Payment" @change="adjustMaxValue(rule, 'min_payment', 'max_payment')">
                  </div>
                  <div class="form-group col-md-2">
                    <input type="number" step="0.01" class="form-control" v-model="rule.amount" placeholder="Amount">
                  </div>
                  <div class="form-group col-md-2 d-flex">
                    <button v-if="idx !== 0" type="button" class="btn btn-danger btn-delete" @click="removeItem('paymentRules', idx, type)">
                      <i class="fa fa-trash"></i>
                    </button>
                    <button v-if="idx === type.paymentRules.length - 1" type="button" class="btn btn-primary btn-add" @click="addItem('paymentRules', type)">
                      <i class="fa fa-plus"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Set as Default -->
            <div class="form-group form-check">
              <input type="checkbox" class="form-check-input" id="isDefault" v-model="commissionSetting.is_default">
              <label class="form-check-label" for="isDefault">Set as default</label>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showModal = false">Close</button>
            <button type="submit" class="btn btn-primary">
              {{ isUpdating ? 'Saving..' : 'Save changes' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import { paymentSettings } from "../../../services/api/commissions";

import '../../../../sass/commissions/payment-settings-modal.scss';

export default {
  name: 'EditCommissionSettingModal',
  components: {
    'v-select': vSelect
  },
  props: {
    userId: {
      type: Number,
      default: 0,
    }
  },
  data() {
    return {
      isUpdating: false,
      showModal: false,
      commissionId: null,
      commissionSetting: {},
      enhancerOptions: [
        { label: 'Key Partner', value: 'key_partner' },
        { label: 'Reference', value: 'reference' },
        { label: 'Self Generated', value: 'self' }
      ],
      enhancerLevelOptions: [
        { label: 'Platinum', value: 'platinum' },
        { label: 'Gold', value: 'gold' },
        { label: 'Silver', value: 'silver' },
        { label: 'Budget', value: 'budget' },
      ],
      schoolTypeOptions: [
        { label: 'Tafe', value: 'tafe' },
        { label: 'Public', value: 'public' },
        { label: 'Higher Education', value: 'higher_education' }
      ]
    };
  },
  computed: {
    filteredEnhancerOptions() {
      return index => {
        const selectedTypes = this.commissionSetting.enhancers
          .filter((enhancer, idx) => idx !== index && enhancer.type)
          .map(enhancer => enhancer.type);

        return this.enhancerOptions.filter(option => !selectedTypes.includes(option.value));
      };
    },
    filteredEnhancerLevelOptions() {
      return index => {
        const enhancersLevel = this.commissionSetting.level_enhancers;
        const selectedLevel = enhancersLevel
          .filter((enhancer, idx) => idx !== index && enhancer.name)
          .map(enhancer => enhancer.name);
        return this.enhancerLevelOptions.filter(option => {
          return selectedLevel.indexOf(option.value) === -1;
        });
      }
    },
    filteredSchoolTypeOptions() {
      return index => {
        const selectedTypes = this.commissionSetting.school_types
          .filter((type, idx) => idx !== index && type.school_type)
          .map(type => type.school_type);

        return this.schoolTypeOptions.filter(option => !selectedTypes.includes(option.value));
      };
    },
    isLevelEnhancersHidden() {
      console.log(this.commissionSetting.level_enhancers_setting)
      return this.commissionSetting.level_enhancers_setting === 0 && this.commissionSetting.closed_commission_count > 0;
    }
  },
  methods: {
    async openModal() {
      try {
        const response = await paymentSettings.details(this.userId, this.commissionId);

        if (response.status === 200) {
          this.commissionSetting = response.data;
          this.mapEnhancerTypes(this.commissionSetting, this.enhancerOptions);
          this.mapEnhancerLevels(this.commissionSetting, this.enhancerLevelOptions);
          this.mapSchoolTypes(this.commissionSetting, this.schoolTypeOptions);

          // Ensure level_enhancers has a default value
          if (!this.commissionSetting.level_enhancers || this.commissionSetting.level_enhancers.length === 0) {
            this.commissionSetting.level_enhancers = [{ name: "", percentage: 0 }];
          }

          // Ensure there's at least one empty school type entry
          if (this.commissionSetting.school_types.length === 0) {
            this.commissionSetting.school_types.push({ school_type: "", paymentRules: [{ min_payment: "", max_payment: "", amount: "" }] });
          }

          this.showModal = true;
        }
      } catch (error) {
        console.error('Error fetching commission setting:', error);

        new Noty({
          type: "error",
          text: 'Failed to get commission payment setting. Please try again later.',
        }).show();
      }
    },
    async updateSetting() {
      try {
        if (this.hasDuplicateEnhancerTypes(this.commissionSetting)) {
          new Noty({
            type: "error",
            text: 'Each enhancer type must be unique.',
          }).show();
          return;
        }
        if (this.hasDuplicateEnhancerLevels(this.commissionSetting)) {
          new Noty({
            type: "error",
            text: 'Duplicate level enhancer types are not allowed.',
          }).show();
          return;
        }

        if (this.hasDuplicateSchoolTypes(this.commissionSetting)) {
          new Noty({
            type: "error",
            text: 'Each school type must be unique.',
          }).show();
          return;
        }

        this.isUpdating = true;

        const payload = {
          ...this.commissionSetting,
          enhancers: this.commissionSetting.enhancers.map(enhancer => ({
            ...enhancer,
            type: enhancer.type.value || enhancer.type
          })),
          level_enhancers: this.commissionSetting.level_enhancers
            .filter(enhancer => enhancer.name.trim() || enhancer.percentage > 0)
            .map(enhancer => ({
              name: enhancer.option?.value || enhancer.name,
              percentage: enhancer.percentage
            })) || null,
          school_types: this.commissionSetting.school_types.map(type => ({
            ...type,
            school_type: type.school_type && type.school_type.value || type.school_type
          }))
        };

        // Remove school_types from payload if they are empty
        if (payload.school_types.every(type => !type.school_type)) {
          delete payload.school_types;
        }

        const response = await paymentSettings.update(this.userId, this.commissionId, payload);

        if (response.status === 200) {
          this.$emit('updated', response.data);
          this.showModal = false;

          new Noty({
            type: "success",
            text: 'Commission payment setting updated successfully',
          }).show();
        }
      } catch (error) {
        console.error('Error updating commission payment setting:', error);

        new Noty({
          type: "error",
          text: 'Failed to update commission payment setting. Please try again later.',
        }).show();
      } finally {
        this.isUpdating = false;
      }
    },
    addItem(type, parentItem = null) {
      if (type === 'rules') {
        const lastIndexRule = this.commissionSetting.rules.length - 1;
        const lastMaxPayment = this.commissionSetting.rules[lastIndexRule]?.max_payment || "";
        const newRule = {
          min_payment: lastMaxPayment ? (Number(lastMaxPayment) + 1) : "",
          max_payment: lastMaxPayment ? (Number(lastMaxPayment) + 1) : "",
          commission: ""
        };
        this.commissionSetting.rules.push(newRule);
      } else if (type === 'enhancers') {
        const newEnhancer = {
          type: "",
          percentage: ""
        };
        this.commissionSetting.enhancers.push(newEnhancer);
      } else if (type === 'level_enhancers') {
        const newLevelEnhancer = {
          name: "",
          percentage: 0
        };
        this.commissionSetting.level_enhancers.push(newLevelEnhancer);
      } else if (type === 'qty_enhancers') {
        const lastIndexQtyEnhancer = this.commissionSetting.qty_enhancers.length - 1;
        const lastMaxStudents = this.commissionSetting.qty_enhancers[lastIndexQtyEnhancer]?.max_students || "";
        const newQtyEnhancer = {
          min_students: lastMaxStudents ? (Number(lastMaxStudents) + 1) : "",
          max_students: lastMaxStudents ? (Number(lastMaxStudents) + 1) : "",
          percentage: ""
        };
        this.commissionSetting.qty_enhancers.push(newQtyEnhancer);
      } else if (type === 'school_types') {
        const newSchoolType = {
          school_type: "",
          paymentRules: [{ min_payment: "", max_payment: "", amount: "" }]
        };
        this.commissionSetting.school_types.push(newSchoolType);
      } else if (type === 'paymentRules' && parentItem) {
        const lastIndexRule = parentItem.paymentRules.length - 1;
        const lastMaxPayment = parentItem.paymentRules[lastIndexRule]?.max_payment || "";
        const newPaymentRule = {
          min_payment: lastMaxPayment ? (Number(lastMaxPayment) + 1) : "",
          max_payment: lastMaxPayment ? (Number(lastMaxPayment) + 1) : "",
          amount: ""
        };
        parentItem.paymentRules.push(newPaymentRule);
      }
    },
    removeItem(type, index, parentItem = null) {
      if (type === 'paymentRules' && parentItem) {
        parentItem.paymentRules.splice(index, 1);
      } else if (type === 'level_enhancers') {
        this.commissionSetting.level_enhancers.splice(index, 1);
      } else {
        this.commissionSetting[type].splice(index, 1);
      }
    },
    adjustMaxValue(item, minProp, maxProp) {
      if (item[maxProp] !== "" && parseFloat(item[maxProp]) < parseFloat(item[minProp])) {
        setTimeout(() => {
          if (item[maxProp] !== "" && parseFloat(item[maxProp]) < parseFloat(item[minProp])) {
            item[maxProp] = item[minProp];
          }
        }, 200);
      }
    },
    mapEnhancerTypes(settings, options) {
      settings.enhancers = settings.enhancers.map(enhancer => {
        let matchingOption = options.find(option => option.value === enhancer.type);
        if (!matchingOption) {
          matchingOption = options.find(option => option.label === enhancer.type);
        }
        return {
          ...enhancer,
          type: matchingOption || enhancer.type
        };
      });
    },
    mapEnhancerLevels(settings, options) {
      settings.level_enhancers = settings.level_enhancers.map(enhancer => {
        let matchingOption = options.find(option => option.value === enhancer.name);
        if (!matchingOption) {
          matchingOption = options.find(option => option.label === enhancer.name);
        }
        return {
          ...enhancer,
          option: matchingOption ? matchingOption : {label: enhancer.name, value: enhancer.name}
        };
      });
    },
    mapSchoolTypes(settings, options) {
      settings.school_types = settings.school_types.map(type => {
        let matchingOption = options.find(option => option.value === type.school_type);
        if (!matchingOption) {
          matchingOption = options.find(option => option.label === type.school_type);
        }
        return {
          ...type,
          school_type: matchingOption || type.school_type
        };
      });
    },
    hasDuplicateEnhancerTypes(settings) {
      const types = settings.enhancers.map(enhancer => enhancer.type.value || enhancer.type);
      return types.some((type, index) => types.indexOf(type) !== index);
    },
    hasDuplicateEnhancerLevels(settings) {
      const levels = settings.level_enhancers.map(enhancer => enhancer.name).filter(level => level !== '');
      return levels.some((level, index) => levels.indexOf(level) !== index);
    },
    hasDuplicateSchoolTypes(settings) {
      const types = settings.school_types.map(type => type.school_type && type.school_type.value || type.school_type);
      return types.some((type, index) => types.indexOf(type) !== index);
    },
    handleSchoolTypeChange(index) {
      const selectedSchoolType = this.commissionSetting.school_types[index];

      if (selectedSchoolType.school_type) {
        if (this.commissionSetting.school_types[index].paymentRules.length === 0) {
          this.commissionSetting.school_types[index].paymentRules.push({ min_payment: "", max_payment: "", amount: "" });
        }
      } else {
        this.commissionSetting.school_types[index].paymentRules = [];
      }
    }
  }
};
</script>
